// Audit log — paginated, filterable by action.

import { useEffect, useState } from "react";
import { gql } from "../lib/gql";

type AuditEntry = {
  id: number;
  userId: string;
  userEmail: string | null;
  action: string;
  targetType: string | null;
  targetId: string | null;
  payload: string | null;
  createdAt: string;
};

const QUERY = `
  query AdminAuditLogs($limit: Int, $offset: Int, $action: String) {
    adminAuditLogs(limit: $limit, offset: $offset, action: $action) {
      id
      userId
      userEmail
      action
      targetType
      targetId
      payload
      createdAt
    }
  }
`;

const PAGE_SIZE = 50;

export function AuditLogPage() {
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [actionFilter, setActionFilter] = useState("");
  const [page, setPage] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<number>>(new Set());

  useEffect(() => {
    (async () => {
      try {
        const data = await gql<{ adminAuditLogs: AuditEntry[] }>(QUERY, {
          limit: PAGE_SIZE,
          offset: page * PAGE_SIZE,
          action: actionFilter || null,
        });
        setEntries(data.adminAuditLogs);
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      }
    })();
  }, [page, actionFilter]);

  function toggle(id: number) {
    const next = new Set(expanded);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setExpanded(next);
  }

  return (
    <div>
      <h2>Аудит</h2>

      <div className="row" style={{ gap: 12, marginBottom: 12 }}>
        <select
          value={actionFilter}
          onChange={(e) => {
            setActionFilter(e.target.value);
            setPage(0);
          }}
          style={{
            background: "var(--bg-elevated)",
            color: "var(--text)",
            border: "1px solid var(--border)",
            padding: "6px 10px",
            borderRadius: 4,
          }}
        >
          <option value="">Все действия</option>
          <option value="admin_invite_partner">admin_invite_partner</option>
          <option value="admin_delete_partner">admin_delete_partner</option>
          <option value="admin_create_admin_user">admin_create_admin_user</option>
          <option value="admin_deactivate_user">admin_deactivate_user</option>
          <option value="admin_import_meta_devices">admin_import_meta_devices</option>
          <option value="admin_migrate_panel">admin_migrate_panel</option>
          <option value="admin_transfer_device">admin_transfer_device</option>
          <option value="admin_replace_rts_camera_module">admin_replace_rts_camera_module</option>
        </select>
        <div className="muted" style={{ fontSize: 12 }}>
          Страница {page + 1} · {entries.length} записей
        </div>
      </div>

      {error && <div className="error-banner">{error}</div>}

      <div className="card">
        {entries.length === 0 ? (
          <div className="muted">Пусто.</div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Когда</th>
                <th>Кто</th>
                <th>Действие</th>
                <th>Объект</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {entries.map((e) => {
                const isExp = expanded.has(e.id);
                return (
                  <>
                    <tr key={e.id}>
                      <td className="muted">
                        {new Date(e.createdAt).toLocaleString("ru-RU")}
                      </td>
                      <td>{e.userEmail || <span className="muted">—</span>}</td>
                      <td>{e.action}</td>
                      <td className="muted">
                        {e.targetType}
                        {e.targetId ? `: ${e.targetId.slice(0, 8)}…` : ""}
                      </td>
                      <td>
                        {e.payload && (
                          <button
                            className="secondary"
                            style={{ fontSize: 11, padding: "2px 6px" }}
                            onClick={() => toggle(e.id)}
                          >
                            {isExp ? "Скрыть" : "JSON"}
                          </button>
                        )}
                      </td>
                    </tr>
                    {isExp && e.payload && (
                      <tr key={`${e.id}-json`}>
                        <td colSpan={5} style={{ background: "var(--bg-elevated)" }}>
                          <pre
                            style={{
                              margin: 0,
                              fontFamily: "monospace",
                              fontSize: 11,
                              whiteSpace: "pre-wrap",
                              wordBreak: "break-word",
                            }}
                          >
                            {formatPayload(e.payload)}
                          </pre>
                        </td>
                      </tr>
                    )}
                  </>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <div className="row" style={{ justifyContent: "center", gap: 8, marginTop: 16 }}>
        <button
          className="secondary"
          disabled={page === 0}
          onClick={() => setPage((p) => Math.max(0, p - 1))}
        >
          ← Назад
        </button>
        <button
          className="secondary"
          disabled={entries.length < PAGE_SIZE}
          onClick={() => setPage((p) => p + 1)}
        >
          Вперёд →
        </button>
      </div>
    </div>
  );
}

function formatPayload(raw: string): string {
  try {
    return JSON.stringify(JSON.parse(raw), null, 2);
  } catch {
    return raw;
  }
}
